import type {
  Ajustes, AjustesPlataforma, Borrador, Capacidad, Caso, Config, Corrida,
  CredencialInfo, DescubrimientoMCP, FilaTenant, Inteligencia, ResultadoPrueba,
  Precio, ResumenCorrida, Usuario, Veredicto, Vigencia,
} from '@/tipos'

// Cliente HTTP del API de administracion.
//
// Sin axios: fetch alcanza para nueve endpoints, y una dependencia menos es una
// dependencia menos que parchear. Lo unico que hace falta encapsular es el token
// y la forma de los errores.

export class ErrorAPI extends Error {
  constructor(public codigo: number, mensaje: string) {
    super(mensaje)
  }
  get esSesionCaida() { return this.codigo === 401 }
  get esPermiso()     { return this.codigo === 403 }
  get esConflicto()   { return this.codigo === 409 }
}

const CLAVE_TOKEN = 'botia.token'

export const token = {
  leer:     () => localStorage.getItem(CLAVE_TOKEN) ?? '',
  guardar:  (t: string) => localStorage.setItem(CLAVE_TOKEN, t),
  olvidar:  () => localStorage.removeItem(CLAVE_TOKEN),
}

async function pedir<T>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  const t = token.leer()
  if (t) cabeceras.Authorization = `Bearer ${t}`
  if (cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json'

  let r: Response
  try {
    r = await fetch(ruta, {
      method: metodo,
      headers: cabeceras,
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    })
  } catch {
    // Distinguir "no hay red" de "el servidor dijo que no" importa: son dos
    // cosas distintas que hacer.
    throw new ErrorAPI(0, 'No se pudo contactar el servidor. ¿Está corriendo la consola?')
  }

  if (r.status === 204) return undefined as T

  const texto = await r.text()
  let datos: any = null
  try { datos = texto ? JSON.parse(texto) : null } catch { /* respuesta no JSON */ }

  if (!r.ok) {
    throw new ErrorAPI(r.status, datos?.error ?? `El servidor respondió ${r.status}`)
  }
  return datos as T
}

export const api = {
  yo: () => pedir<Usuario>('GET', '/api/yo'),

  catalogo: () => pedir<{ capacidades: Capacidad[] }>('GET', '/api/catalogo'),

  tenants: () => pedir<{ tenants: FilaTenant[] }>('GET', '/api/tenants'),

  vigente: (id: string) => pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}`),

  borrador: (id: string) => pedir<Borrador>('GET', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  guardarBorrador: (id: string, config: Config, base_version: number, nota = '') =>
    pedir<{ guardado: boolean; problemas: string[] }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/borrador`, { config, base_version, nota }),

  descartarBorrador: (id: string) =>
    pedir<{ descartado: boolean }>('DELETE', `/api/tenants/${encodeURIComponent(id)}/borrador`),

  validar: (id: string, config: Config) =>
    pedir<{ problemas: string[] }>('POST', `/api/tenants/${encodeURIComponent(id)}/validar`, { config }),

  probar: (id: string, config: Config, mensajes: string[], escrituras = false) =>
    pedir<ResultadoPrueba>('POST', `/api/tenants/${encodeURIComponent(id)}/probar`,
      { config, mensajes, escrituras }),

  versiones: (id: string) =>
    pedir<{ versiones: Vigencia[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/versiones`),

  version: (id: string, v: number) =>
    pedir<Config>('GET', `/api/tenants/${encodeURIComponent(id)}/versiones/${v}`),

  crearVersion: (id: string, nota: string) =>
    pedir<{ version: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/versiones`, { nota }),

  publicar: (id: string, version: number, motivo: string, forzar = false) =>
    pedir<{ publicada: number }>('POST', `/api/tenants/${encodeURIComponent(id)}/publicar`,
      { version, motivo, forzar }),

  despublicar: (id: string, motivo: string) =>
    pedir<{ despublicado: boolean }>('POST', `/api/tenants/${encodeURIComponent(id)}/despublicar`, { motivo }),

  secretos: (id: string) =>
    pedir<{ secretos: Array<{ Nombre: string; Cifrado: boolean; Autor: string; Nota: string; Actualizado: string }> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/secretos`),

  guardarSecreto: (id: string, nombre: string, valor: string, nota = '') =>
    pedir<{ guardado: boolean; caracteres: number; cifrado: boolean; recortado: number }>(
      'PUT', `/api/tenants/${encodeURIComponent(id)}/secretos/${encodeURIComponent(nombre)}`, { valor, nota }),

  sesiones: (id: string) =>
    pedir<{ sesiones: Array<{ conversacion: string; paso: string; turnos: number; actualizada: string }> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/sesiones`),

  reiniciarSesion: (id: string, conversacion: string) =>
    pedir<{ reiniciada: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/sesiones/${encodeURIComponent(conversacion)}`),

  salidas: (id: string, para = '', limite = 100) => {
    const q = new URLSearchParams({ limite: String(limite) })
    if (para) q.set('para', para)
    return pedir<{ salidas: Array<Record<string, unknown>> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/salidas?${q}`)
  },

  // -------------------------------------------------------------------------
  // Evaluaciones
  // -------------------------------------------------------------------------

  casos: (id: string) =>
    pedir<{ casos: Caso[] }>('GET', `/api/tenants/${encodeURIComponent(id)}/casos`),

  guardarCaso: (id: string, caso: Partial<Caso>) =>
    pedir<{ id: string }>('POST', `/api/tenants/${encodeURIComponent(id)}/casos`, caso),

  borrarCaso: (id: string, caso: string) =>
    pedir<{ borrado: boolean }>(
      'DELETE', `/api/tenants/${encodeURIComponent(id)}/casos/${encodeURIComponent(caso)}`),

  // Propone un caso a partir de una conversación real. No lo guarda: quien la
  // convierte sabe por qué la está guardando, y esa intención es justo lo que
  // hay que escribir en los invariantes.
  casoDesdeConversacion: (id: string, conversacion: string, horas = 24) =>
    pedir<{ caso: Caso; aviso: string }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/casos/desde-conversacion`,
      { conversacion, horas }),

  evaluar: (id: string, config?: Config, casos: string[] = [], guardar = false) =>
    pedir<{ problemas: string[]; corrida: Corrida; avisos?: string[] }>(
      'POST', `/api/tenants/${encodeURIComponent(id)}/evaluar`, { config, casos, guardar }),

  corridas: (id: string, limite = 30) =>
    pedir<{ corridas: ResumenCorrida[] }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas?limite=${limite}`),

  corrida: (id: string, corrida: string) =>
    pedir<Corrida>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/corridas/${encodeURIComponent(corrida)}`),

  // -------------------------------------------------------------------------
  // Inteligencia
  // -------------------------------------------------------------------------
  // Estos ajustes NO se versionan: aplican al guardar. Es deliberado — apagar
  // la IA de un cliente a las once de la noche no puede exigir publicar.

  inteligencia: (id: string) =>
    pedir<Inteligencia>('GET', `/api/tenants/${encodeURIComponent(id)}/inteligencia`),

  guardarInteligencia: (id: string, ajustes: Ajustes) =>
    pedir<Inteligencia>('PUT', `/api/tenants/${encodeURIComponent(id)}/inteligencia`, ajustes),

  // Los globales sí: mueven a los treinta clientes a la vez.
  plataforma: () => pedir<AjustesPlataforma>('GET', '/api/plataforma/ajustes'),

  guardarPlataforma: (ajustes: Ajustes, motivo: string, publicar = false) =>
    pedir<{ version: number; publicada: boolean }>(
      'PUT', '/api/plataforma/ajustes', { ajustes, motivo, publicar }),

  publicarPlataforma: (version: number, motivo: string) =>
    pedir<{ publicada: number }>('POST', '/api/plataforma/ajustes', { version, motivo }),

  // -------------------------------------------------------------------------
  // Credenciales de los proveedores de modelo
  // -------------------------------------------------------------------------
  // No hay método para leer una clave, y no es un olvido: el API no la devuelve
  // por ningún camino. Se registra y se reemplaza.

  credenciales: (tenant?: string) =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean }>(
      'GET', rutaCredenciales(tenant)),

  // Todas las de todos los clientes. Responde "¿quién factura contra su propio
  // contrato y quién contra el nuestro?" sin abrir treinta pantallas.
  // Los precios se administran aquí, no en el .env: un proveedor los cambia sin
  // avisarnos, y ajustarlos no puede exigir un reinicio.
  precios: () =>
    pedir<{ precios: Precio[]; ayuda: string }>('GET', '/api/plataforma/precios'),

  guardarPrecio: (p: Precio) =>
    pedir<{ guardado: boolean }>('PUT', '/api/plataforma/precios', p),

  borrarPrecio: (proveedor: string, modelo: string) =>
    pedir<{ borrado: boolean }>('DELETE',
      `/api/plataforma/precios/${encodeURIComponent(proveedor)}/${encodeURIComponent(modelo)}`),

  // La puerta para pasar a modo agente. No prohíbe: informa.
  promocion: (id: string) =>
    pedir<{ veredicto: Veredicto; evidencia: Record<string, unknown> }>(
      'GET', `/api/tenants/${encodeURIComponent(id)}/promocion`),

  // Lo que hace falta para conectar un agente, y qué vería si se conecta.
  mcp: (id: string) =>
    pedir<DescubrimientoMCP>('GET', `/api/tenants/${encodeURIComponent(id)}/mcp`),

  credencialesTodas: () =>
    pedir<{ credenciales: CredencialInfo[]; cifradas: boolean }>(
      'GET', '/api/plataforma/credenciales?todas=1'),

  guardarCredencial: (tenant: string | undefined, c: {
    proveedor: string; clave: string; base_url?: string; organizacion?: string; nota?: string
  }) =>
    pedir<{ guardada: boolean; cifrada: boolean; caracteres: number; recortados: number }>(
      'PUT', rutaCredenciales(tenant), c),

  activarCredencial: (tenant: string | undefined, proveedor: string, activa: boolean) =>
    pedir<{ activa: boolean }>(
      'POST', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`, { activa }),

  borrarCredencial: (tenant: string | undefined, proveedor: string) =>
    pedir<{ borrada: boolean }>(
      'DELETE', `${rutaCredenciales(tenant)}/${encodeURIComponent(proveedor)}`),
}

// Sin tenant son las de la plataforma: la cuenta contra la que factura todo el
// que no traiga la suya.
function rutaCredenciales(tenant?: string) {
  return tenant
    ? `/api/tenants/${encodeURIComponent(tenant)}/credenciales`
    : '/api/plataforma/credenciales'
}
